import { muscleGroups } from "../data/muscleGroups";
import { getExerciseLog } from "../utils/localStorageUtils";
import { formatYMDLocal } from "../utils/dateUtils";
import { getAllExercisesCatalog } from "./customExerciseService";

/**
 * Returns the last N dates in YYYY-MM-DD format
 * @param {number} n Number of last dates.
 * @returns {Array<string>} Array of the last n dates.
 */
export function getLastNDates(n) {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < n; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        dates.push(formatYMDLocal(date)); // Format YYYY-MM-DD in local time
    }

    return dates;
}

/**
 * Calculates the number of sets for muscle groups for each date.
 * @param {Array<string>} dates Array of dates.
 * @param {Object} log Exercise log data (optional).
 * @param {Object} initialCounts Initial counts object (optional).
 * @returns {Object} Data about the number of sets for each muscle group for each date.
 */
export function calculateMuscleGroupCountsByDate(dates, log, initialCounts) {
    // Use provided log or get it from localStorage
    const exerciseLog = log || getExerciseLog();
    // Use provided initialCounts or create a new object
    const counts = initialCounts || {};

    // Create a dictionary of exercises for quick access
    const exerciseMap = getAllExercisesCatalog().reduce((map, exercise) => {
        map[exercise.id] = exercise;
        return map;
    }, {});

    // Initialize counts for each muscle group if not already initialized
    if (Object.keys(counts).length === 0) {
        muscleGroups.forEach((group) => {
            counts[group.id] = {};
        });
    }

    muscleGroups.forEach((group) => {
        // Make sure this muscle group is initialized in counts
        if (!counts[group.id]) {
            counts[group.id] = {};
        }

        dates.forEach((date) => {
            // Skip if this date is already processed for this muscle group
            if (counts[group.id][date]) {
                return;
            }
            
            const dateLog = exerciseLog[date] || {};
            let primaryCount = 0;
            let secondaryCount = 0;

            // Iterate through all exercises in logs for the given date
            Object.keys(dateLog).forEach((exerciseId) => {
                const exercise = exerciseMap[exerciseId];
                if (exercise) {
                    const primaryExercises = exercise.muscleGroups.filter(
                        (mg) => mg.group === group.id && mg.priority === "primary"
                    );
                    if (primaryExercises.length > 0) {
                        primaryCount += dateLog[exerciseId].length;
                    }

                    const secondaryExercises = exercise.muscleGroups.filter(
                        (mg) => mg.group === group.id && mg.priority === "secondary"
                    );
                    if (secondaryExercises.length > 0) {
                        secondaryCount += dateLog[exerciseId].length;
                    }
                }
            });

            counts[group.id][date] = { primary: primaryCount, secondary: secondaryCount };
        });
    });

    return counts;
}
